import {
  Autocomplete,
  Box,
  CircularProgress,
  StandardTextFieldProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { equals, F } from "ramda";

export default forwardRef(function ScrollAsyncAutocomplete(
  {
    search,
    loading,
    options,
    onLoadMore,
    hasMore,
    label,
    value,
    onChange,
    disabled,
    variant,
    error,
    textFieldProps,
    disableClearable = false,
    disableCloseOnSelect = false,
  }: {
    search: (value: string) => void;
    loading: boolean;
    options: SelectOptions;
    onLoadMore: () => void;
    hasMore: boolean;
    label?: string;
    value: SelectOptions[number];
    onChange: (value: SelectOptions[number]["value"]) => void;
    disabled?: boolean;
    variant?: StandardTextFieldProps["variant"];
    error?: string;
    textFieldProps?: TextFieldProps;
    disableClearable?: boolean;
    disableCloseOnSelect?: boolean;
  },
  ref: any
) {
  const [displayedOptions, setDisplayedOptions] = useState(options);
  const [inputValue, setInputValue] = useState(value?.label ?? "");
  useEffect(() => {
    search("");
  }, []);
  useEffect(() => {
    if (options && !loading) {
      setDisplayedOptions(options);
    }
  }, [options, loading]);
  useEffect(() => {
    setInputValue(value?.label ?? "");
  }, [value?.value]);

  const observer = useRef<IntersectionObserver>();
  const lastOption = options?.slice(-1)?.pop();
  const isLastOption = lastOption ? equals(lastOption) : F;

  const lastOptionElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(async (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore]
  );

  return (
    <>
      <Autocomplete
        filterOptions={(x) => x}
        options={displayedOptions}
        value={value ?? null}
        inputValue={inputValue}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        isOptionEqualToValue={(option, value) =>
          Boolean(option?.value) && Boolean(value?.value) && option.value === value.value
        }
        disableClearable={disableClearable}
        disableCloseOnSelect={disableCloseOnSelect}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "clear") {
            setInputValue("");
            search("");
            return;
          }
          if (reason === "input") {
            setInputValue(newInputValue ?? "");
            search(newInputValue ?? "");
            return;
          }
          if (reason === "reset") {
            // Ignore MUI internal resets so search typing is not overwritten on rerender.
            return;
          }
          setInputValue(newInputValue ?? "");
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            return false;
          }
          return true;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? "Search"}
            variant={variant ?? "outlined"}
            error={!!error}
            helperText={error}
            {...textFieldProps}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps?.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <>
            <Box
              key={option.value}
              component="li"
              {...(isLastOption(option) ? { ref: lastOptionElementRef } : {})}
              {...props}
            >
              {option.label}
            </Box>
            {loading && isLastOption(option) && (
              <Box component="li" sx={{ pointerEvents: "none" }}>
                <CircularProgress color="inherit" size={16} />
              </Box>
            )}
          </>
        )}
        onChange={(_, selected) => {
          if (typeof selected === "string") {
            setInputValue(selected);
            onChange(selected);
            return;
          }

          setInputValue(selected?.label ?? "");
          onChange(selected?.value || null);
        }}
      />
    </>
  );
});
